import { FormControlLabel, Paper, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useRef, useState } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import styles from "@/components/trackedService/trackedService.module.css";
import { RadioButtonsWithRef } from "@/components/forms/inputsWithRef/radioButtonsWithRef";
import GenericControllerWithInput from "@/components/forms/inputsWithController/GenericController";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import DropDown from "@/components/forms/inputs/dropDown";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import RestrictedPage from "@/consumer/restrictedPage";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import GenericDateRange from "@/components/dateTime/genericDateRange";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { KAER_COMPANY_SUBJECT_ID } from "@/helper/systemIds";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { IDropDownOption } from "@/types/DropDown";

type CompanySubject = {
  companyId: string;
  isParent: boolean;
  is_active: boolean;
  label: string;
  value: string;
};

type FormValues = {
  company_subject:
    | { value: string; label: string }
    | string
    | CompanySubject
    | null;
  subject: { value: string; label: string } | string | null;
  on_site: boolean;
  description: string;
  estimated_duration: number;
  actual_duration: number;
  vat: number;
  price: number;
  // relevant_for_timesheet: boolean | { value: boolean; label: string };
  category: string;
  tracked_service_type: string;
  tracked_service_template: string;
  info_finance: string;
  info_ops: string;
  billed_by: string;
  checked_by: string;
  executed_by: string | number;
  executed_on: string | Date | Dayjs;
  building: { value: string; label: string } | string | null;
  // metadata: any;
  // appointment: any;
  // billed_on: Date;
  // checked_on: Date;
};
type Mode = "create" | "edit";

const trackedServiceFieldValidationOrder: Array<keyof FormValues> = [
  "company_subject",
  "tracked_service_template",
  "subject",
  "estimated_duration",
  "price",
  "category",
  "tracked_service_type",
  "actual_duration",
  "executed_on",
];

const getSelectedValue = (
  selection: { value: string } | string | null | undefined,
) => {
  return typeof selection === "string" ? selection : selection?.value;
};

const getRelatedId = (relation: { id: string } | string | null | undefined) => {
  return typeof relation === "string" ? relation : relation?.id;
};

const toSubjectOption = (
  selection: FormValues["company_subject"],
): FormValues["subject"] => {
  return typeof selection === "object" && selection !== null
    ? { value: selection.value, label: selection.label }
    : null;
};

const templateDrivenFields: Array<keyof FormValues> = [
  "description",
  "estimated_duration",
  "price",
  "tracked_service_type",
  "category",
];

const fieldsOverwrittenOnTemplateChange: Array<keyof FormValues> = [
  ...templateDrivenFields,
  "actual_duration",
];
const templateValueHighlightDurationMs = 2500;

type TemplateValues = Partial<Record<keyof FormValues, unknown>>;

const getOptionValue = (selection: unknown): string | number | undefined => {
  if (typeof selection === "string" || typeof selection === "number") {
    return selection;
  }
  if (
    typeof selection === "object" &&
    selection !== null &&
    "value" in selection
  ) {
    const { value } = selection as { value: unknown };
    return typeof value === "string" || typeof value === "number"
      ? value
      : undefined;
  }
  return undefined;
};

const resolveDropDownOption = (
  selection: unknown,
  options: IDropDownOption[],
): IDropDownOption | null => {
  const selectedValue = getOptionValue(selection);
  if (selectedValue === undefined) return null;
  return options.find((option) => option.value === selectedValue) ?? null;
};

const normalizeForComparison = (value: unknown): string => {
  const rawValue = getOptionValue(value) ?? value;
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return "";
  }
  const numericValue = Number(String(rawValue).replace(",", "."));
  return Number.isNaN(numericValue) ? String(rawValue) : String(numericValue);
};

const haveDifferentValues = (previousValue: unknown, nextValue: unknown) =>
  normalizeForComparison(previousValue) !== normalizeForComparison(nextValue);

const toTemplateOption = (template: any): IDropDownOption => ({
  value: template.id,
  label: template.internal_name,
});

type TemplatesForCompanySubject = {
  companySubjectId: string;
  templates: any[];
};

const TrackedServiceCustomCrud: FC = () => {
  const { t } = useTranslation();
  const { handleError, errorModal } = useErrorModal();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const [formMode, setFormMode] = useState<Mode>("create");
  usePageHeading(
    formMode === "create"
      ? t("trackedservice_create")
      : t("trackedservice_edit"),
  );
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [trackedServiceTypeOptions, setTrackedServiceTypeOptions] = useState<
    any[]
  >([]);
  const [trackedServiceTemplateOptions, setTrackedServiceTemplateOptions] =
    useState<any[]>([]);
  const [kaerEmployeesOptions, setKaerEmployeesOptions] = useState<any[]>([]);
  const [buildingOptions, setBuildingOptions] = useState<any[]>([]);
  const [buildingResetSignal, setBuildingResetSignal] = useState<number>();
  const [
    personRequiredAfterCompanyChange,
    setPersonRequiredAfterCompanyChange,
  ] = useState(false);
  const [isPersonFieldHidden, setIsPersonFieldHidden] = useState(false);
  const [highlightedTemplateFields, setHighlightedTemplateFields] = useState<
    Array<keyof FormValues>
  >([]);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [templatesForCompanySubject, setTemplatesForCompanySubject] =
    useState<TemplatesForCompanySubject>();

  // const relevantForTimesheetOptions = [
  //   { value: true, label: t("yes") },
  //   { value: false, label: t("no") },
  // ];

  const { trackedServiceId } = useParams() as { trackedServiceId: string };
  const navigate = useNavigate();
  const loginStateForExecutedBy = useSelector(
    (state: RootState) => state.login,
  );

  const { control, handleSubmit, watch, setValue, getValues } =
    useForm<FormValues>({
      defaultValues: {},
      shouldFocusError: false,
    });
  const { focusFirstInvalidField, registerValidationTarget } =
    useFocusFirstInvalidField<FormValues>({
      fieldOrder: trackedServiceFieldValidationOrder,
    });
  const previousBuildingRef = useRef<any>(null);
  const previousCompanySubjectIdRef = useRef<string>();
  const existingEntryHadPersonRef = useRef(false);
  const existingEntryBookedOnCompanyRef = useRef(false);

  const rawCompanySubject = watch("company_subject");

  const isCompanySubject = (value: unknown): value is CompanySubject => {
    return (
      typeof value === "object" &&
      value !== null &&
      "companyId" in value &&
      "value" in value &&
      "label" in value
    );
  };
  const companySubject = isCompanySubject(rawCompanySubject)
    ? rawCompanySubject
    : null;
  const selectedCompanySubjectId =
    typeof rawCompanySubject === "object" &&
    rawCompanySubject !== null &&
    "value" in rawCompanySubject
      ? (rawCompanySubject as { value: string }).value
      : undefined;
  const trackedServiceTemplate = watch("tracked_service_template");
  const shouldShowFields =
    formMode === "edit" || (rawCompanySubject && trackedServiceTemplate);
  useEffect(() => {
    getMainCategories(setCategoryOptions, handleError);
    getKaerEmployees(setKaerEmployeesOptions, handleError);
    getTrackedServiceTypes(setTrackedServiceTypeOptions, handleError);
  }, []);

  useEffect(() => {
    if (trackedServiceId === undefined) {
      getTrackedServiceTemplates(setTrackedServiceTemplateOptions, handleError);
    }
  }, [trackedServiceId]);

  useEffect(() => {
    if (formMode !== "edit" || selectedCompanySubjectId === undefined) return;
    getTrackedServiceTemplatesForCompanySubject(
      selectedCompanySubjectId,
      (templates) => {
        setTemplatesForCompanySubject({
          companySubjectId: selectedCompanySubjectId,
          templates,
        });
        setTrackedServiceTemplateOptions(templates.map(toTemplateOption));
      },
      handleError,
    );
  }, [formMode, selectedCompanySubjectId]);

  useEffect(() => {
    if (
      rawCompanySubject !== undefined &&
      rawCompanySubject !== null &&
      typeof rawCompanySubject !== "string" &&
      rawCompanySubject.value !== undefined
    ) {
      getBuildings(setBuildingOptions, rawCompanySubject.value, handleError);
    }
  }, [rawCompanySubject]);

  useEffect(() => {
    if (trackedServiceId !== undefined) {
      api.genericApiRequest({
        method: "get",
        entity: "trackedService",
        entityId: trackedServiceId,
        successHandler: (res: any) => {
          const loadedCompanySubjectId = getRelatedId(res.data.company_subject);
          const loadedSubjectId = getRelatedId(res.data.subject);
          const isBookedOnCompany =
            loadedSubjectId !== undefined &&
            loadedSubjectId === loadedCompanySubjectId;
          previousCompanySubjectIdRef.current = loadedCompanySubjectId;
          existingEntryBookedOnCompanyRef.current = isBookedOnCompany;
          existingEntryHadPersonRef.current = res.data.subject != null;
          setPersonRequiredAfterCompanyChange(false);
          setIsPersonFieldHidden(isBookedOnCompany);
          const fieldsToUpdateOnEdit: Array<keyof FormValues> = [
            "company_subject",
            "tracked_service_template",
            "subject",
            "on_site",
            "description",
            "estimated_duration",
            "actual_duration",
            "vat",
            "price",
            // "relevant_for_timesheet",
            "category",
            "tracked_service_type",
            "info_ops",
            "info_finance",
            "executed_by",
            "executed_on",
            "building",
          ];

          Object.entries(res.data).forEach(([key, value]) => {
            if (fieldsToUpdateOnEdit.includes(key as keyof FormValues)) {
              let finalValue:
                | string
                | number
                | boolean
                | { value: any; label: string }
                | Date
                | dayjs.Dayjs
                | undefined;

              if (key === "executed_on" && typeof value === "string") {
                finalValue = dayjs(value);
              } else if (key === "company_subject" || key === "subject") {
                if (value === null || value === undefined) {
                  finalValue = undefined;
                } else if (
                  typeof value === "object" &&
                  "id" in value &&
                  "name" in value
                ) {
                  finalValue = {
                    value: (value as { id: any }).id,
                    label: (value as { name: string }).name,
                  };
                }
              } else if (
                ["tracked_service_template"].includes(key) &&
                value !== null &&
                typeof value === "object" &&
                "id" in value
              ) {
                finalValue = (value as { id: any }).id;
              } else {
                finalValue =
                  typeof value === "object" && value !== null && "id" in value
                    ? (value as { id: any }).id
                    : (value as string | number | boolean);
              }

              if (finalValue !== undefined) {
                setValue(key as keyof FormValues, finalValue);
              }
              if (key === "building") {
                previousBuildingRef.current = finalValue;
              }
            }
          });
          // if (res.data.relevant_for_timesheet == null) {
          //   getRelevantForTimesheetFromTemplate(
          //     res.data.tracked_service_template?.id,
          //     setValue,
          //     handleError,
          //   );
          // }
          setFormMode("edit");
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    }
  }, [trackedServiceId]);
  useEffect(() => {
    if (trackedServiceId === undefined) {
      previousCompanySubjectIdRef.current = undefined;
      setBuildingResetSignal(undefined);
      existingEntryHadPersonRef.current = false;
      existingEntryBookedOnCompanyRef.current = false;
      setPersonRequiredAfterCompanyChange(false);
      setIsPersonFieldHidden(false);
      setFormMode("create");
    }
  }, [trackedServiceId]);

  useEffect(() => {
    if (trackedServiceTemplate && trackedServiceId === undefined) { // the route, not formMode: loading an entry renders its template before formMode turns to "edit"
      getTrackedServiceTemplateValues(
        trackedServiceTemplate,
        setValue,
        handleError,
      );
    }
  }, [trackedServiceTemplate, trackedServiceId]);

  useEffect(() => {
    const executedBy = watch("executed_by");
    const executedOn = watch("executed_on");
    const onSite = watch("on_site");
    if (formMode === "create") {
      if (executedBy == null) {
        setValue("executed_by", loginStateForExecutedBy.subjectId);
      }
      if (executedOn == null) {
        setValue("executed_on", dayjs.utc().startOf("day").toISOString());
      }
      if (onSite == null) {
        setValue("on_site", false);
      }
    }
  }, [formMode]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const highlightTemplateFields = (fields: Array<keyof FormValues>) => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setHighlightedTemplateFields(fields);
    if (fields.length === 0) return;
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedTemplateFields([]);
    }, templateValueHighlightDurationMs);
  };

  const getTemplateFieldHighlightClass = (field: keyof FormValues) =>
    highlightedTemplateFields.includes(field)
      ? styles.templateValueHighlight
      : undefined;

  const handleTemplateChange = async (
    newTemplate: IDropDownOption | null,
    applyTemplateSelection: (template: IDropDownOption | null) => void,
  ) => {
    if (formMode !== "edit") {
      applyTemplateSelection(newTemplate);
      return;
    }
    const newTemplateId = getOptionValue(newTemplate);
    if (
      newTemplateId === undefined ||
      newTemplateId === getOptionValue(getValues("tracked_service_template"))
    ) {
      return;
    }

    const isConfirmed = await showConfirmationModalAsync(
      t("trackedservice_template_change_confirm"),
      { confirmButtonText: t("yes"), cancelButtonText: t("no") },
    );
    if (!isConfirmed) return;

    const previousValues: TemplateValues = {};
    fieldsOverwrittenOnTemplateChange.forEach((field) => {
      previousValues[field] = getValues(field);
    });
    const handleTemplateValuesApplied = (templateValues: TemplateValues) => {
      const appliedValues: TemplateValues = { ...templateValues };
      if (templateValues.estimated_duration !== undefined) {
        setValue(
          "actual_duration",
          templateValues.estimated_duration as FormValues["actual_duration"],
        );
        appliedValues.actual_duration = templateValues.estimated_duration;
      }
      highlightTemplateFields(
        fieldsOverwrittenOnTemplateChange.filter(
          (field) =>
            field in appliedValues &&
            haveDifferentValues(previousValues[field], appliedValues[field]),
        ),
      );
    };

    highlightTemplateFields([]);
    applyTemplateSelection(newTemplate);

    const currentCompanySubjectId = getSelectedValue(
      getValues("company_subject"),
    );
    const templateForCompany =
      templatesForCompanySubject &&
      templatesForCompanySubject.companySubjectId === currentCompanySubjectId
        ? templatesForCompanySubject.templates.find(
            (template) => template.id === newTemplateId,
          )
        : undefined;
    if (templateForCompany) {
      applyTemplateValues(
        templateForCompany,
        setValue,
        handleTemplateValuesApplied,
      );
      return;
    }
    getTrackedServiceTemplateValues(newTemplate, setValue, handleError, {
      companySubjectId: currentCompanySubjectId,
      onValuesApplied: handleTemplateValuesApplied,
    });
  };

  const submitTrackedService = (data: any) => {
    const addIfDefined = (targetObject: any, keyToAssign: any, value: any) => {
      if (value !== undefined) targetObject[keyToAssign] = value;
    };
    const { price, company_subject, subject, building, ...restData } = data;
    const result: any = { ...restData };
    const companySubjectId = getSelectedValue(company_subject);
    const subjectId = getSelectedValue(subject);
    const buildingId = getSelectedValue(building);

    if (formMode === "edit") {
      result.price = price;
      addIfDefined(result, "company_subject", companySubjectId);
      result.subject = subjectId ?? null;
      result.building = buildingId ?? null;
    } else {
      addIfDefined(
        result,
        "company_subject_set",
        companySubjectId === undefined ? undefined : [companySubjectId],
      );
      addIfDefined(result, "subject", subjectId);
      addIfDefined(result, "building", buildingId);
    }

    addIfDefined(
      result,
      "on_site",
      data.on_site === "yes" || data.on_site === true ? true : false,
    );
    addIfDefined(result, "executed_by", data.executed_by?.value);
    addIfDefined(
      result,
      "tracked_service_template",
      data.tracked_service_template?.value,
    );
    addIfDefined(
      result,
      "tracked_service_type",
      data.tracked_service_type?.value,
    );
    addIfDefined(result, "category", data.category?.value);
    // addIfDefined(
    //   result,
    //   "relevant_for_timesheet",
    //   data.relevant_for_timesheet?.value,
    // );
    if (formMode === "create") {
      postTrackedService(
        "post",
        result,
        failHandlerForPost,
        successHandlerForPost,
      );
    } else {
      postTrackedService(
        "put",
        result,
        failHandlerForPost,
        successHandlerForPost,
        trackedServiceId,
      );
    }
    previousBuildingRef.current = data?.building;
  };
  const successHandlerForPost = () => {
    navigate("/trackedservice/recorded/");
  };
  const failHandlerForPost = (error: any) => {
    handleError(error);
  };

  const handleInvalidFormSubmit = (fieldErrors: FieldErrors<FormValues>) => {
    focusFirstInvalidField(fieldErrors);
  };

  const handleBuildingChange = (newBuilding: any) => {
    if (newBuilding && newBuilding?.value !== previousBuildingRef?.current) {
      setValue("on_site", true, { shouldValidate: true });
    } else {
      setValue("on_site", false, { shouldValidate: true });
    }
  };

  return (
    <RestrictedPage permissionRequired="frontend_view_tracked_service">
      <section
        aria-labelledby="form-wrapper"
        style={{
          width: "100%",
        }}
      >
        <>
          <Tooltip
            placement="right"
            title={t("back_to", { name: t("company") })}
          >
            <GenericButton
              variant="icon"
              color="iconBase"
              onClick={successHandlerForPost}
            >
              <ArrowBackIcon />
            </GenericButton>
          </Tooltip>
          <Paper className={styles.formPaperWrapper}>
            <form
              onSubmit={handleSubmit(
                submitTrackedService,
                handleInvalidFormSubmit,
              )}
              noValidate
              className={styles.form}
            >
              <GenericControllerWithInput
                control={control}
                name="company_subject"
                rules={{
                  required: {
                    value: true,
                    message: t("field_required"),
                  },
                }}
                renderInput={({ onChange, ref, value }) => {
                  const handleCompanySubjectChange = (
                    newCompanySubject: FormValues["company_subject"],
                  ) => {
                    const newCompanySubjectId =
                      getSelectedValue(newCompanySubject);
                    if (
                      newCompanySubjectId !==
                      previousCompanySubjectIdRef.current
                    ) {
                      if (
                        formMode === "edit" &&
                        existingEntryHadPersonRef.current
                      ) {
                        setPersonRequiredAfterCompanyChange(true);
                      }
                      const subjectAfterCompanyChange =
                        existingEntryBookedOnCompanyRef.current
                          ? toSubjectOption(newCompanySubject)
                          : null;
                      setValue("subject", subjectAfterCompanyChange, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      setValue("building", null, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      setBuildingOptions([]);
                      setBuildingResetSignal((currentSignal) =>
                        currentSignal === undefined ? 0 : currentSignal + 1,
                      );
                      previousBuildingRef.current = null;
                    }
                    previousCompanySubjectIdRef.current = newCompanySubjectId;
                    onChange(newCompanySubject);
                  };

                  return (
                    <div ref={registerValidationTarget("company_subject")}>
                      <FormFieldLabel
                        label={t("company")}
                        htmlFor="company_subject"
                        mandatory
                      />
                      <DropDownSubjectSearch
                        name="company_subject"
                        customRef={ref}
                        onChange={handleCompanySubjectChange}
                        subdomain={"admin"}
                        queryParameter={"companies"}
                        isClearable={true}
                        useGlobalState={false}
                        preExistingValue={value || null}
                      />
                    </div>
                  );
                }}
              />
              {formMode === "create" && rawCompanySubject && (
                <GenericControllerWithInput
                  control={control}
                  name="subject"
                  renderInput={({ onChange, ref, value }) => (
                    <div ref={registerValidationTarget("subject")}>
                      <FormFieldLabel label={t("person")} htmlFor="subject" />
                      <DropDownSubjectSearch
                        key={selectedCompanySubjectId ?? "unselected-company"}
                        name="subject"
                        customRef={ref}
                        onChange={onChange}
                        subdomain={"admin"}
                        queryParameter={"users"}
                        isClearable={true}
                        useGlobalState={false}
                        preExistingValue={value || null}
                        companyIdForFiltering={companySubject?.companyId}
                        subjectIdForFiltering={
                          companySubject?.companyId
                            ? undefined
                            : selectedCompanySubjectId
                        }
                      />
                    </div>
                  )}
                />
              )}
              <GenericControllerWithInput
                control={control}
                name="tracked_service_template"
                rules={{
                  required: {
                    value: true,
                    message: t("field_required"),
                  },
                }}
                renderInput={({ onChange, value }) => (
                  <div
                    ref={registerValidationTarget("tracked_service_template")}
                  >
                    <DropDown
                      options={trackedServiceTemplateOptions}
                      onChange={(newTemplate) =>
                        handleTemplateChange(newTemplate, onChange)
                      }
                      name="tracked_service_template"
                      label={t("trackedservice_template")}
                      externalValue={resolveDropDownOption(
                        value,
                        trackedServiceTemplateOptions,
                      )}
                      mandatory={true}
                    />
                  </div>
                )}
              />
              {shouldShowFields && (
                <>
                  {formMode === "edit" && (
                    <>
                      {isPersonFieldHidden ? (
                        <GenericButton
                          variant="outlined"
                          className={styles.changePersonButton}
                          onClick={() => setIsPersonFieldHidden(false)}
                        >
                          {t("trackedservice_change_person")}
                        </GenericButton>
                      ) : (
                        <GenericControllerWithInput
                          control={control}
                          name="subject"
                          rules={
                            personRequiredAfterCompanyChange
                              ? {
                                  required: {
                                    value: true,
                                    message: t("field_required"),
                                  },
                                }
                              : undefined
                          }
                          renderInput={({ onChange, ref, value }) => (
                            <div ref={registerValidationTarget("subject")}>
                              <FormFieldLabel
                                label={t("person")}
                                htmlFor="subject"
                                mandatory={personRequiredAfterCompanyChange}
                              />
                              <DropDownSubjectSearch
                                key={
                                  selectedCompanySubjectId ??
                                  "unselected-company"
                                }
                                name="subject"
                                customRef={ref}
                                onChange={onChange}
                                subdomain={"admin"}
                                queryParameter={"users"}
                                isClearable={true}
                                useGlobalState={false}
                                preExistingValue={value || null}
                                companyIdForFiltering={
                                  companySubject?.companyId
                                }
                                subjectIdForFiltering={
                                  companySubject?.companyId
                                    ? undefined
                                    : selectedCompanySubjectId
                                }
                              />
                            </div>
                          )}
                        />
                      )}
                      <GenericControllerWithInput
                        control={control}
                        name="building"
                        renderInput={({ onChange, value }) => (
                          <DropDown
                            options={buildingOptions}
                            resetSignal={buildingResetSignal}
                            // onChange={onChange}
                            onChange={(newValue) => {
                              if (newValue === undefined) return;
                              onChange(newValue);
                              handleBuildingChange(newValue);
                            }}
                            name="building"
                            label={t("building_location")}
                            defaultValue={value}
                          />
                        )}
                      />
                      <GenericControllerWithInput
                        control={control}
                        name="description"
                        renderInput={({ onChange, ref, value }) => (
                          <div
                            className={getTemplateFieldHighlightClass(
                              "description",
                            )}
                          >
                            <TextAreaWithRef
                              label={t("trackedservice_description_nameing")}
                              name="description"
                              onChange={onChange}
                              ref={ref}
                              value={value}
                            />
                          </div>
                        )}
                      />
                      <GenericControllerWithInput
                        control={control}
                        name="estimated_duration"
                        rules={{
                          required: {
                            value: true,
                            message: t("field_required"),
                          },
                        }}
                        renderInput={({ onChange, ref, value }) => (
                          <div
                            ref={registerValidationTarget("estimated_duration")}
                            className={getTemplateFieldHighlightClass(
                              "estimated_duration",
                            )}
                          >
                            <InputWithRef
                              name="estimated_duration"
                              onChange={onChange}
                              label={t("trackedservice_estimated_duration")}
                              type="int"
                              ref={ref}
                              value={value}
                              mandatory={true}
                            />
                          </div>
                        )}
                      />

                      <GenericControllerWithInput
                        control={control}
                        name="price"
                        rules={{
                          required: {
                            value: true,
                            message: t("field_required"),
                          },
                          pattern: {
                            value: /^\d+([.,]\d{1,2})?$/,
                            message: t("invalid_price_format"),
                          },
                        }}
                        renderInput={({ onChange, ref, value }) => {
                          const displayValue = value ?? "";

                          return (
                            <div
                              ref={registerValidationTarget("price")}
                              className={getTemplateFieldHighlightClass(
                                "price",
                              )}
                            >
                              <InputWithRef
                                name="price"
                                onChange={(e) => {
                                  const val = e.target.value.replace(
                                    /[^0-9,.]/g,
                                    "",
                                  );
                                  onChange(val);
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  if (!val) return;

                                  const numeric = parseFloat(
                                    val.replace(",", "."),
                                  );
                                  if (!isNaN(numeric)) {
                                    onChange(
                                      numeric.toFixed(2).replace(".", ","),
                                    );
                                  }
                                }}
                                label={t("price_with_currency", {
                                  currencySymbol: "€",
                                })}
                                ref={ref}
                                value={displayValue}
                                min="0"
                                mandatory={true}
                                onWheel={(e: any) => {
                                  e.currentTarget.blur();
                                }}
                              />
                            </div>
                          );
                        }}
                      />
                      <GenericControllerWithInput
                        control={control}
                        name="vat"
                        renderInput={({ onChange, ref, value }) => (
                          <InputWithRef
                            name="vat"
                            onChange={(e) => {
                              const raw = e.target.value;

                              if (raw === "") {
                                onChange("");
                                return;
                              }

                              let val = parseFloat(raw);

                              if (val < 0) val = 0;

                              onChange(val);
                            }}
                            label={t("vat_short")}
                            type="float"
                            ref={ref}
                            value={value}
                            onWheel={(e: any) => {
                              e.currentTarget.blur();
                            }}
                          />
                        )}
                      />
                      {/* <GenericControllerWithInput
                        control={control}
                        name="relevant_for_timesheet"
                        rules={{
                          validate: (value) =>
                            (value !== undefined && value !== null) ||
                            t("field_required"),
                        }}
                        renderInput={({ onChange, value }) => (
                          <DropDown
                            options={relevantForTimesheetOptions}
                            onChange={onChange}
                            name="relevant_for_timesheet"
                            label={t("relevant_for_timesheet")}
                            defaultValue={value}
                            mandatory={true}
                          />
                        )}
                      /> */}
                      <GenericControllerWithInput
                        control={control}
                        name="category"
                        rules={{
                          required: {
                            value: true,
                            message: t("field_required"),
                          },
                        }}
                        renderInput={({ onChange, value }) => (
                          <div
                            ref={registerValidationTarget("category")}
                            className={getTemplateFieldHighlightClass(
                              "category",
                            )}
                          >
                            <DropDown
                              options={categoryOptions}
                              onChange={onChange}
                              name="category"
                              label={t("category")}
                              externalValue={resolveDropDownOption(
                                value,
                                categoryOptions,
                              )}
                              mandatory={true}
                            />
                          </div>
                        )}
                      />
                      <GenericControllerWithInput
                        control={control}
                        name="tracked_service_type"
                        rules={{
                          required: {
                            value: true,
                            message: t("field_required"),
                          },
                        }}
                        renderInput={({ onChange, value }) => (
                          <div
                            ref={registerValidationTarget(
                              "tracked_service_type",
                            )}
                            className={getTemplateFieldHighlightClass(
                              "tracked_service_type",
                            )}
                          >
                            <DropDown
                              options={trackedServiceTypeOptions}
                              onChange={onChange}
                              name="tracked_service_type"
                              label={t("trackedservice_type")}
                              externalValue={resolveDropDownOption(
                                value,
                                trackedServiceTypeOptions,
                              )}
                              mandatory={true}
                            />
                          </div>
                        )}
                      />

                      <GenericControllerWithInput
                        control={control}
                        name="info_finance"
                        renderInput={({ onChange, ref, value }) => (
                          <TextAreaWithRef
                            label={t("trackedservice_info_finance")}
                            name="info_finance"
                            onChange={onChange}
                            ref={ref}
                            value={value}
                          />
                        )}
                      />
                    </>
                  )}

                  <GenericControllerWithInput
                    control={control}
                    name="actual_duration"
                    rules={{
                      required: {
                        value: true,
                        message: t("field_required"),
                      },
                    }}
                    renderInput={({ onChange, ref, value }) => (
                      <div
                        ref={registerValidationTarget("actual_duration")}
                        className={getTemplateFieldHighlightClass(
                          "actual_duration",
                        )}
                      >
                        <InputWithRef
                          name="actual_duration"
                          onChange={onChange}
                          label={t("trackedservice_actual_duration")}
                          type="int"
                          ref={ref}
                          value={value || undefined}
                          mandatory={true}
                        />
                      </div>
                    )}
                  />

                  <GenericControllerWithInput
                    control={control}
                    name="info_ops"
                    renderInput={({ onChange, ref, value }) => (
                      <TextAreaWithRef
                        label={t("trackedservice_info_ops")}
                        name="info_ops"
                        onChange={onChange}
                        ref={ref}
                        value={value}
                      />
                    )}
                  />
                  <GenericControllerWithInput
                    control={control}
                    name="executed_on"
                    rules={{
                      required: {
                        value: true,
                        message: t("field_required"),
                      },
                    }}
                    renderInput={({ onChange, value }) => {
                      const handleDateChange = (e: any) => {
                        if (!e) {
                          onChange("");
                          return;
                        }
                        const formattedValue = dayjs
                          .utc(dayjs(e).format("YYYY-MM-DD"))
                          .startOf("day")
                          .toISOString();

                        onChange(formattedValue);
                      };
                      return (
                        <div ref={registerValidationTarget("executed_on")}>
                          <FormControlLabel
                            labelPlacement="top"
                            disableTypography
                            sx={{
                              display: "flex",
                              margin: "0",
                              alignItems: "start",
                            }}
                            label={
                              <section>
                                {t("executed_on")}
                                <span style={{ color: "red" }}>*</span>
                              </section>
                            }
                            control={
                              <GenericDateRange
                                selectionMode="single"
                                className={styles.datePicker}
                                value={value ? dayjs(value) : null}
                                onChange={handleDateChange}
                                onClear={() => {
                                  setValue("executed_on", "");
                                }}
                              />
                            }
                          />
                        </div>
                      );
                    }}
                  />

                  <GenericControllerWithInput
                    control={control}
                    name="executed_by"
                    renderInput={({ onChange, value }) => (
                      <DropDown
                        options={kaerEmployeesOptions}
                        onChange={onChange}
                        name="executed_by"
                        label={t("executed_by")}
                        defaultValue={value}
                        mandatory={true}
                      />
                    )}
                  />
                  <GenericControllerWithInput
                    control={control}
                    name="on_site"
                    renderInput={({ onChange, ref, value }) => (
                      <RadioButtonsWithRef
                        key={value}
                        name={"on_site"}
                        label={t("trackedservice_on_site")}
                        onChange={onChange}
                        ref={ref}
                        defaultValue={value}
                      />
                    )}
                  />
                </>
              )}

              <GenericButton type="submit" className={styles.submitButton}>
                {formMode === "edit" ? t("save") : t("create")}
              </GenericButton>
            </form>
          </Paper>
        </>
        {errorModal}
      </section>
    </RestrictedPage>
  );
};

export default TrackedServiceCustomCrud;

function getKaerEmployees(setKaerEmployeesOptions: any, handleError: any) {
  const additionalUserParameters: any = {};
  additionalUserParameters["impersonate_subject"] = KAER_COMPANY_SUBJECT_ID;
  additionalUserParameters["is_active"] = "true";
  additionalUserParameters["include_relations"] = true;

  api.genericApiRequest({
    entity: "user",
    method: "get",
    getAllPages: true,

    parametersToRender: {
      additionalUrlParameters: additionalUserParameters,
      depth: "0",
    },

    successHandler: (res: any) => {
      const employeeOptions = res.data.results.map((item: any) => ({
        value: item.person.subject,
        label: item.last_name + ", " + item.first_name,
      }));

      setKaerEmployeesOptions(
        employeeOptions.sort((a: any, b: any) =>
          a.label.localeCompare(b.label),
        ),
      );
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}
function getMainCategories(setCategoryOptions: any, handleError: any) {
  api.genericApiRequest({
    entity: "mainCategory",
    method: "get",
    getAllPages: true,

    successHandler: (res: any) => {
      const mainCategoryOptions = res.data.results.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      setCategoryOptions(mainCategoryOptions);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}
function getTrackedServiceTypes(
  setTrackedServiceTypeOptions: any,
  handleError: any,
) {
  api.genericApiRequest({
    entity: "trackedServiceType",
    method: "get",
    getAllPages: true,

    successHandler: (res: any) => {
      const trackedServiceTypeOptions = res.data.results.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      setTrackedServiceTypeOptions(trackedServiceTypeOptions);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}
function getTrackedServiceTemplates(
  setTrackedServiceTemplateOptions: any,
  handleError: any,
) {
  api.genericApiRequest({
    entity: "trackedServiceTemplate",
    method: "get",
    getAllPages: true,
    successHandler: (res: any) => {
      setTrackedServiceTemplateOptions(res.data.results.map(toTemplateOption));
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}

function getTrackedServiceTemplatesForCompanySubject(
  companySubjectId: string,
  onLoaded: (templates: any[]) => void,
  handleError: any,
) {
  api.genericApiRequest({
    entity: "trackedServiceTemplate",
    method: "get",
    additionalRouteParts: { fetch_templates: "/" },
    getAllPages: true,
    parametersToRender: {
      additionalUrlParameters: { company_subject_id: companySubjectId },
    },
    successHandler: (res: any) => {
      onLoaded(res?.data?.results ?? []);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}
function getBuildings(
  setBuildingOptions: any,
  subject: string,
  handleError: any,
) {
  const additionalUserParameters: any = {};
  additionalUserParameters["subject"] = subject;
  api.genericApiRequest({
    entity: "building",
    method: "get",
    getAllPages: true,

    parametersToRender: {
      additionalUrlParameters: additionalUserParameters,
      depth: "0",
    },

    successHandler: (res: any) => {
      const buildingOptions = res.data.results.map((item: any) => ({
        value: item.id,
        label:
          item.name + ` (${item.street_house}, ${item.zip} ${item?.city} )`,
      }));
      setBuildingOptions(buildingOptions);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}

type TemplateValueLoadOptions = {
  companySubjectId?: string;
  onValuesApplied?: (templateValues: TemplateValues) => void;
};

function readTemplateFieldValue(templateData: any, field: keyof FormValues) {
  let rawValue: unknown;
  if (field === "price") {
    rawValue = templateData.price ?? templateData.original_price;
  } else if (field === "estimated_duration") {
    rawValue =
      templateData.estimated_duration ??
      templateData.original_estimated_duration;
  } else {
    rawValue = templateData[field];
  }

  return typeof rawValue === "object" && rawValue !== null && "id" in rawValue
    ? (rawValue as { id: any }).id
    : rawValue;
}

function applyTemplateValues(
  templateData: any,
  setFormValue: any,
  onValuesApplied?: (templateValues: TemplateValues) => void,
) {
  const appliedValues: TemplateValues = {};

  templateDrivenFields.forEach((field) => {
    const finalValue = readTemplateFieldValue(templateData, field);
    if (finalValue === undefined) return;

    setFormValue(field, finalValue);
    appliedValues[field] = finalValue;
  });
  onValuesApplied?.(appliedValues);
}

function getTrackedServiceTemplateValues(
  templateId: any,
  setFormValue: any,
  handleError: any,
  options: TemplateValueLoadOptions = {},
) {
  const { companySubjectId, onValuesApplied } = options;
  const selectedTemplateId = templateId.value;

  const loadTemplateWithoutCompanyOverrides = () => {
    api.genericApiRequest({
      method: "get",
      entity: "trackedServiceTemplate",
      entityId: selectedTemplateId,
      getAllPages: true,

      successHandler: (res: any) => {
        applyTemplateValues(res.data, setFormValue, onValuesApplied);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  if (!companySubjectId) {
    loadTemplateWithoutCompanyOverrides();
    return;
  }

  api.genericApiRequest({
    method: "get",
    entity: "trackedServiceTemplate",
    additionalRouteParts: { fetch_templates: "/" },
    getAllPages: true,
    parametersToRender: {
      additionalUrlParameters: {
        company_subject_id: companySubjectId,
        id: selectedTemplateId,
      },
    },

    successHandler: (res: any) => {
      const templateForCompany = (res?.data?.results ?? []).find(
        (template: any) => template.id === selectedTemplateId,
      );
      if (!templateForCompany) {
        loadTemplateWithoutCompanyOverrides();
        return;
      }
      applyTemplateValues(templateForCompany, setFormValue, onValuesApplied);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}

// function getRelevantForTimesheetFromTemplate(
//   templateId: any,
//   setFormValue: any,
//   handleError: any,
// ) {
//   if (templateId == null) return;

//   api.genericApiRequest({
//     method: "get",
//     entity: "trackedServiceTemplate",
//     entityId: templateId,
//     successHandler: (res: any) => {
//       if (res.data.relevant_for_timesheet != null) {
//         setFormValue("relevant_for_timesheet", res.data.relevant_for_timesheet);
//       }
//     },
//     failHandler: (error: any) => {
//       handleError(error);
//     },
//   });
// }

function postTrackedService(
  method: any,
  submitData: any,
  failHandler: any,
  successHandler: any,
  entityId?: any,
) {
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  const requestParams = {
    method: method,
    entity: "trackedService",
    submitData: submitData,
    successHandler: () => {
      successHandler();
    },
    failHandler: (error: any) => {
      failHandler(error);
    },
    config: config,
    ...(method === "put" && entityId ? { entityId } : {}),
  };

  api.genericApiRequest(requestParams);
}
